/**
 * Admin Analytics Overview – bento stats and dark cards (matches Finance page)
 */
import React, { useEffect, useState, useRef } from 'react';
import { useAdmin } from '../../hooks/useAdmin';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Spinner from '../common/Spinner';
import Select from '../common/Select';

const AdminAnalyticsOverview = () => {
  const { loadDashboardMetrics, dashboardMetrics, loading } = useAdmin();
  const { showError } = useToast();
  const { formatCurrency, formatNumber, language } = useI18n();
  const [timeRange, setTimeRange] = useState('30d');
  const [statsInView, setStatsInView] = useState(false);
  const statsRef = useRef(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        await loadDashboardMetrics({ timeRange });
      } catch (error) {
        showError(
          error.message || (language === 'fr' ? 'Échec du chargement' : 'Failed to load')
        );
      }
    };
    loadData();
  }, [timeRange, loadDashboardMetrics, showError, language]);

  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => e.isIntersecting && setStatsInView(true));
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  if (loading && !dashboardMetrics) {
    return (
      <div className="transactions-wt-loading">
        <div className="dashboard-wt-hero-inner">
          <Spinner size="lg" />
          <p className="mt-4 text-[#a3a3a3]">
            {language === 'fr' ? 'Chargement des métriques...' : 'Loading metrics...'}
          </p>
        </div>
      </div>
    );
  }

  const metrics = dashboardMetrics?.metrics || dashboardMetrics || {};
  const transactions = metrics.transactions || {};
  const financial = metrics.financial || {};
  const users = metrics.users || {};
  const disputes = metrics.disputes || {};
  const escrow = metrics.escrow || {};

  const totalVolume = Number(transactions.totalValue) || Number(financial.totalRevenue) || 0;
  const activeTransactions = Number(transactions.active) || 0;
  const newUsers = Number(users.newUsers) || 0;
  const activeDisputes = Number(disputes.open) || 0;
  const revenue = Number(financial.totalRevenue) || 0;
  const completed = Number(transactions.completed) || 0;
  const activeEscrows = Number(escrow.fundedCount) || Number(escrow.totalCount) || 0;
  const successRate = (Number(transactions.successRate) || 0).toFixed(1);

  const statCards = [
    { icon: '💰', label: language === 'fr' ? 'Volume Total' : 'Total Volume', value: formatCurrency(totalVolume, 'XAF') },
    { icon: '📦', label: language === 'fr' ? 'Transactions Actives' : 'Active Transactions', value: formatNumber(activeTransactions) },
    { icon: '👥', label: language === 'fr' ? 'Nouveaux Utilisateurs' : 'New Users', value: formatNumber(newUsers) },
    { icon: '⚠️', label: language === 'fr' ? 'Litiges Actifs' : 'Active Disputes', value: formatNumber(activeDisputes) },
    { icon: '💳', label: language === 'fr' ? 'Revenus' : 'Revenue', value: formatCurrency(revenue, 'XAF') },
    { icon: '✅', label: language === 'fr' ? 'Complétées' : 'Completed', value: formatNumber(completed) },
    { icon: '🔒', label: language === 'fr' ? 'Escrows Actifs' : 'Active Escrows', value: formatNumber(activeEscrows) },
    { icon: '📈', label: language === 'fr' ? 'Taux de Réussite' : 'Success Rate', value: `${successRate}%` },
  ];

  return (
    <>
      {/* Time range filter */}
      <div className="dashboard-wt-card mb-6">
        <div className="dashboard-wt-card-header">
          <h2 className="dashboard-wt-card-title">
            📊 {language === 'fr' ? 'Période' : 'Time Range'}
          </h2>
        </div>
        <div className="dashboard-wt-card-content">
          <div className="transactions-wt-form-row">
            <div className="transactions-wt-form-select" style={{ maxWidth: 200 }}>
              <Select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                options={[
                  { value: '24h', label: language === 'fr' ? '24 heures' : '24 Hours' },
                  { value: '7d', label: language === 'fr' ? '7 jours' : '7 Days' },
                  { value: '30d', label: language === 'fr' ? '30 jours' : '30 Days' },
                  { value: '90d', label: language === 'fr' ? '90 jours' : '90 Days' },
                  { value: '1y', label: language === 'fr' ? '1 an' : '1 Year' },
                ]}
                className="w-full"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Bento stats */}
      <div className={`dashboard-wt-stats ${statsInView ? 'is-inview' : ''}`} ref={statsRef}>
        {statCards.map((stat, i) => (
          <div
            key={i}
            className="dashboard-wt-stat-card"
            style={{ animationDelay: `${i * 0.08}s` }}
          >
            <div className="dashboard-wt-stat-icon">
              <span>{stat.icon}</span>
            </div>
            <div className="dashboard-wt-stat-content">
              <div className="dashboard-wt-stat-value">{stat.value}</div>
              <div className="dashboard-wt-stat-label">{stat.label}</div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default AdminAnalyticsOverview;
